import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Building2, PieChart, Coins, TrendingUp, ArrowUpRight, Scale, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { Badge } from "../../../../components/ui/Badge";
import AuditField from "./AuditField";

export default function BlocPersonnesMorales({ data }) {
  const navigate = useNavigate();
  const societes = data.personnesMorales || [];

  const formatCurrency = (val) => {
    if (!val || isNaN(val)) return "—";
    const num = Number(val);
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(".", ",")} M€`;
    if (num >= 1_000) return `${Math.round(num / 1_000)} k€`;
    return `${num.toLocaleString("fr-FR")} €`;
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Banner Introductif Fiscalité IS vs IR */}
      <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-primary">Personnes Morales Détenues (IS & SCI)</h2>
            <p className="text-sm text-subtle mt-0.5">
              Les membres physiques du foyer peuvent détenir des parts de sociétés. L'Impôt sur les Sociétés (IS) s'applique 
              au niveau de ces structures, tandis que les dividendes (PFU 30%), rémunérations de gérance et comptes courants d'associés (CCA) 
              remontent vers l'Impôt sur le Revenu (IR) du Foyer Fiscal.
            </p>
          </div>
        </div>
      </div>

      {/* --- LISTE DES SOCIÉTÉS DÉTENUES --- */}
      {societes.length > 0 ? (
        <div className="grid grid-cols-1 gap-5">
          {societes.map((soc, idx) => (
            <div 
              key={soc.id || idx}
              className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm hover:border-[var(--hp-color-accent)] transition-all"
            >
              {/* En-tête Société */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-workspace gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface-muted flex items-center justify-center text-primary font-bold text-base border border-workspace shrink-0">
                    <Building2 className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 
                        onClick={() => soc.id && navigate(`/prospects/societes/${soc.id}`)}
                        className={`text-base font-bold text-primary ${soc.id ? "hover:text-secondary cursor-pointer transition-colors" : ""}`}
                      >
                        {soc.denomination || "Société Patrimoniale"}
                      </h3>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-surface-muted rounded text-muted">
                        {soc.formeJuridique || "SAS"}
                      </span>
                    </div>
                    <p className="text-xs text-subtle mt-0.5">
                      {soc.siren ? `SIREN ${soc.siren}` : "Structure Patrimoniale Familiale"} • {soc.activite || "Holding & Participations"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto">
                  <Badge variant={soc.regimeFiscal?.includes("IR") ? "neutral" : "accent"} className="text-xs">
                    <Scale className="w-3 h-3 mr-1" />
                    {soc.regimeFiscal || "Impôt sur les Sociétés (IS)"}
                  </Badge>
                  <Badge variant="info" className="text-xs">
                    {soc.pourcentageDetention ?? 100}% détenu
                  </Badge>
                  {soc.id && (
                    <button
                      onClick={() => navigate(`/prospects/societes/${soc.id}`)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark px-2.5 py-1 rounded-lg bg-secondary/10 hover:bg-secondary/20 transition-colors"
                    >
                      Détails
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Matrice de Détention & Chiffres Clés */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 pt-4">
                <AuditField 
                  label="Valorisation des Parts" 
                  value={formatCurrency(soc.valeurEstimee || soc.capitalSocial || 0)} 
                  className="font-bold text-primary"
                />
                <AuditField 
                  label="Détenteur(s) dans le Foyer" 
                  value={soc.detenuPar || "Déclarant 1 & Conjoint"} 
                />
                <AuditField 
                  label="Chiffre d'Affaires Annuel" 
                  value={soc.chiffreAffaires ? formatCurrency(soc.chiffreAffaires) : "Holding (Dividendes)"} 
                />
                <AuditField 
                  label="Résultat Net / Trésorerie" 
                  value={soc.resultatNet ? formatCurrency(soc.resultatNet) : "Capitalisation IS"} 
                />
              </div>

              {/* Passerelle Fiscale IS -> IR */}
              <div className="mt-4 p-3.5 rounded-lg bg-surface-muted/30 border border-workspace flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 text-subtle">
                  <Coins className="w-4 h-4 text-warning-600 shrink-0" />
                  <span>
                    <strong>Arbitrage de flux vers l'IR :</strong> Dividendes éligibles Flat Tax (30%) ou Rémunération gérance déductible de l'IS.
                  </span>
                </div>
                <span className="font-semibold text-muted shrink-0 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-success-600" />
                  Régime fiscal optimisé
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="workspace-card p-8 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface text-center space-y-3">
          <Building2 className="w-10 h-10 text-subtle mx-auto opacity-50" />
          <h3 className="text-base font-bold text-primary">Aucune Personne Morale Rattachée</h3>
          <p className="text-sm text-subtle max-w-md mx-auto">
            Ce foyer détient actuellement l'ensemble de son patrimoine en direct (nom propre soumis à l'IR). 
            La création d'une holding ou d'une SCI à l'IS peut être envisagée lors du prochain audit patrimonial.
          </p>
        </div>
      )}
    </motion.section>
  );
}
